
class SwiperContainer extends HTMLElement {
	constructor() {
		super();

		this.defaultSettings = {
			updateOnWindowResize: true,
			watchOverflow: true,
			on: {
				init: this.onInit.bind(this),
				destroy: this.onDestroy.bind(this),
				slideChangeTransitionEnd: this.updateSlidesFocusability.bind(this),
				resize: this.updateSlidesFocusability.bind(this),
				slidesLengthChange: this.updateSlidesFocusability.bind(this),
				// Swiper's a11y module flips the wrapper back to aria-live="polite" on these
				autoplayStop: this.silenceWrapper.bind(this),
				autoplayPause: this.silenceWrapper.bind(this),
			},
		};
	}

	connectedCallback() {
		this.breakpoint = this.dataset.breakpoint;
		this.breakpointMediaQuery = window.matchMedia(this.breakpoint);

		this.createInstance();

		// Re-check slide visibility when the carousel goes from hidden to shown (cart drawer, accordions)
		if ('ResizeObserver' in window) {
			this.resizeObserver = new ResizeObserver(() => this.updateSlidesFocusability());
			this.resizeObserver.observe(this);
		}
	}

	disconnectedCallback() {
		if (this.resizeObserver) this.resizeObserver.disconnect();
	}

	createInstance() {
		try {
			if (typeof Swiper === 'undefined') {
				throw new Error('Swiper is not defined. Ensure Swiper library is included before this script.');
			}
			
			const settings = this.dataset.settings;

			this.settingsJSON = settings ? JSON.parse(settings) : {};
			// Don't let focus slide the carousel; keyboard users move it with the prev/next buttons
			const a11y = Object.assign({}, this.settingsJSON.a11y, { scrollOnFocus: false });
			this.swiper = new Swiper(this, Object.assign({}, this.settingsJSON, this.defaultSettings, { a11y }));
		} catch (error) {
			throw new Error(`Failed to create swiper instance: ${this}; Error description: ${error}`);
		}
	}

	onInit(swiper) {
		// Swiper fires init before the constructor returns, so this.swiper is not set yet
		requestAnimationFrame(() => {
			this.updateSlidesFocusability(swiper);
			this.silenceWrapper(swiper);
		});

		if (this.breakpoint) {
			this.onBreakpointEnable(this.breakpointMediaQuery);
			this.breakpointMediaQuery.addEventListener('change', this.onBreakpointEnable.bind(this));
		}
	}

	/*
	* Screen readers (WCAG 4.1.3): Swiper makes the whole slide track a polite live region, so any
	* re-render (cart upsells refresh on every cart change) reads out every product card. Its own
	* .swiper-notification already announces "slide x of y", so the track itself stays quiet.
	* */
	silenceWrapper(swiper) {
		swiper = swiper || this.swiper;
		if (!swiper || swiper.destroyed || !swiper.wrapperEl) return;
		requestAnimationFrame(() => swiper.wrapperEl.setAttribute('aria-live', 'off'));
	}

	/*
	* Keyboard a11y (WCAG 2.1.1): make slides that are out of view inert so Tab only
	* reaches the visible slides, then moves on past the carousel (e.g. to Checkout)
	* instead of cycling through loop duplicates forever. Prev/next buttons stay focusable.
	* */
	updateSlidesFocusability(swiper) {
		swiper = swiper || this.swiper;
		if (!swiper || swiper.destroyed || !swiper.slides) return;

		const containerRect = this.getBoundingClientRect();
		swiper.slides.forEach((slide) => {
			const rect = slide.getBoundingClientRect();
			const visibleWidth = Math.min(rect.right, containerRect.right) - Math.max(rect.left, containerRect.left);
			// Only fully visible slides are tabbable, so focusing one never scrolls the track
			const isVisible = rect.width > 0 && visibleWidth >= rect.width - 2;
			slide.inert = !isVisible;
			if (isVisible) {
				slide.removeAttribute('aria-hidden');
			} else {
				slide.setAttribute('aria-hidden', 'true');
			}
		});
	}

	onDestroy(swiper) {
		this.querySelectorAll('.swiper-slide').forEach((slide) => {
			slide.inert = false;
			slide.removeAttribute('aria-hidden');
		});

		this.breakpointMediaQuery.removeEventListener('change', this.onBreakpointEnable.bind(this));
	}

	/*
	* Example of usage: data-breakpoint="(max-width: 767px)"
	* */
	onBreakpointEnable(breakpoint) {
		if (breakpoint.matches) {
			if (!this.swiper || this.swiper.destroyed) this.createInstance();
		} else {
			if (this.swiper) this.swiper.destroy(true, true);
		}
	}
}

if (!customElements.get('swiper-container')) {
	customElements.define('swiper-container', SwiperContainer);
}